// Department Configuration for Rhino-1 Enterprise
import {
  Target, TrendingUp, Cog, Phone, Users, Calculator, Scale,
  Truck, LineChart, HardHat, Car, Building2, Server, GraduationCap,
  HeartHandshake, Handshake, FolderKanban, Lightbulb, Home, Leaf
} from 'lucide-react';

// Category orders for sorting agents within departments
export const categoryOrders = {
  Marketing: ['Leadership', 'Strategy', 'Content & Creative', 'Paid Media', 'Analytics & Optimization', 'Customer Journey / PR / Community'],
  Operations: ['Leadership', 'Scheduling & Workflow', 'Field Operations', 'Supply Chain & Logistics', 'Customer-Facing Ops', 'Documentation & Reporting'],
  Sales: ['Leadership', 'Sales Reps', 'Lead Intake & Qualification', 'Pipeline & Sales Ops', 'Marketing-Sales Integration', 'Customer Experience', 'Admin & Support'],
  'Call Center': ['Leadership', 'Inbound Team', 'Outbound Team', 'Appointment Setting', 'Customer Success', 'Quality & Compliance', 'Analytics & Systems', 'Specialty Support'],
  'Human Resources': ['Leadership & Compliance', 'Recruiting & Talent Acquisition', 'Onboarding & Offboarding', 'Employee Relations & Support', 'Training & Culture', 'Payroll & Benefits Support', 'Performance & Analytics', 'Administration'],
  'Finance & Accounting': ['Leadership', 'Core Accounting', 'Job Costing & Profitability', 'Compliance & Audit', 'Forecasting & Reporting', 'Tax & Assets'],
  'Legal & Compliance': ['Leadership', 'Contracts & Documentation', 'Regulatory Compliance', 'Risk & Investigations', 'Claims', 'Administration']
};

// Get category order for a specific department
export const getCategoryOrder = (dept) => categoryOrders[dept] || [];

// Departments with specific filtering capability
export const departmentsWithCategories = [
  'Marketing', 'Operations', 'Sales', 'Call Center', 
  'Human Resources', 'Finance & Accounting', 'Legal & Compliance'
];

// Placeholder departments for future development
export const placeholderDepartments = [
  { id: 'procurement', name: 'Procurement & Supply Chain', icon: Truck, emoji: '📦', description: 'Manages vendor relationships, purchasing, inventory, and supply chain logistics.' },
  { id: 'data-science', name: 'Business Intelligence & Data Science', icon: LineChart, emoji: '📊', description: 'Handles data analytics, reporting, predictive modeling, and business insights.' },
  { id: 'safety', name: 'Safety & Field Compliance', icon: HardHat, emoji: '🦺', description: 'Ensures workplace safety, field compliance, and regulatory adherence.' },
  { id: 'fleet', name: 'Fleet & Equipment Management', icon: Car, emoji: '🚗', description: 'Manages company vehicles, equipment maintenance, and asset tracking.' },
  { id: 'executive', name: 'Executive Leadership (CEO / COO / CFO Suite)', icon: Building2, emoji: '🏛️', description: 'Strategic leadership, executive decision-making, and corporate governance.' },
  { id: 'it-security', name: 'IT & Systems / Cybersecurity', icon: Server, emoji: '🔐', description: 'Information technology, systems administration, and cybersecurity operations.' },
  { id: 'training', name: 'Training & Development', icon: GraduationCap, emoji: '🎓', description: 'Employee training, skill development, and professional growth programs.' },
  { id: 'customer-experience', name: 'Customer Experience (CX)', icon: HeartHandshake, emoji: '💝', description: 'Customer satisfaction, experience optimization, and loyalty programs.' },
  { id: 'vendor-partner', name: 'Vendor & Partner Management', icon: Handshake, emoji: '🤝', description: 'Partner relationships, vendor negotiations, and alliance management.' },
  { id: 'pmo', name: 'Project Management Office (PMO)', icon: FolderKanban, emoji: '📋', description: 'Project governance, resource allocation, and portfolio management.' },
  { id: 'product-innovation', name: 'Product & Innovation', icon: Lightbulb, emoji: '💡', description: 'Product development, innovation initiatives, and R&D activities.' },
  { id: 'real-estate', name: 'Real Estate & Facilities Management', icon: Home, emoji: '🏢', description: 'Property management, facilities maintenance, and real estate operations.' },
  { id: 'environmental', name: 'Environmental & Sustainability', icon: Leaf, emoji: '🌱', description: 'Environmental compliance, sustainability initiatives, and green operations.' },
];

// Department icon and emoji mapping for active departments
export const departmentConfig = {
  Sales: { icon: Target, emoji: '💰', name: 'Sales Department' },
  Marketing: { icon: TrendingUp, emoji: '🎯', name: 'Marketing Department' },
  Operations: { icon: Cog, emoji: '⚙️', name: 'Operations Department' },
  'Call Center': { icon: Phone, emoji: '📞', name: 'Call Center Department' },
  'Human Resources': { icon: Users, emoji: '👥', name: 'Human Resources Department' },
  'Finance & Accounting': { icon: Calculator, emoji: '💵', name: 'Finance & Accounting Department' },
  'Legal & Compliance': { icon: Scale, emoji: '⚖️', name: 'Legal & Compliance Department' }
};

// Get department display info
export const getDepartmentInfo = (deptId) => {
  return departmentConfig[deptId] || { icon: Cog, emoji: '📁', name: deptId };
};

// Department filter display text
export const getDepartmentFilterText = (dept) => {
  const config = departmentConfig[dept];
  return config ? `${config.emoji} ${config.name}` : dept;
};
